/**
 * Packing an ordered stop list into an event's single `location.address` field,
 * and reading it back.
 */

/**
 * Separator the wizard joins stops with. A comma cannot do the job — a formatted
 * place is already "Miami, FL, USA" — so the joiner has to be a character Places
 * never returns inside an address.
 */
export const STOP_SEP = ' | ';

/**
 * Split a legacy comma-joined address back into stops. Before `STOP_SEP` the
 * wizard joined formatted places with ", ", so "Miami, FL, USA" + "Newport, RI,
 * USA" arrived as one flat comma list, and splitting on the comma made every
 * state and country its own "stop" — a pin on the US centroid, an uncharted "FL".
 *
 * Places formats a place as City[, Region][, Country], so a token carrying a
 * lowercase letter starts a new stop only once the current one already holds a
 * region or a country.
 *
 * ponytail: heuristic — it mis-groups a 4-part venue address or two bare city
 * names in a row. Re-saving the event writes `STOP_SEP` and this stops being
 * consulted; it goes away entirely when the backend grows a stops array.
 */
const legacyStops = (address: string): string[] => {
  const groups: string[][] = [];
  for (const part of address.split(',').map((p) => p.trim())) {
    if (!part) continue;
    const last = groups[groups.length - 1];
    if (!last || (/[a-z]/.test(part) && last.length >= 2)) groups.push([part]);
    else last.push(part);
  }
  return groups.map((g) => g.join(', '));
};

/**
 * Ordered stop names from an event's location: the `STOP_SEP` list when the
 * address carries one, the legacy regrouping otherwise, and the plain location
 * name when there is no address at all.
 */
export const parseStops = (address?: string, fallbackName?: string): string[] => {
  const packed = address?.trim();
  const raw = !packed
    ? [fallbackName ?? '']
    : packed.includes(STOP_SEP.trim())
      ? packed.split(STOP_SEP.trim()).map((s) => s.trim())
      : legacyStops(packed);
  // Consecutive duplicates read as one stop, not two.
  const out: string[] = [];
  for (const name of raw.filter(Boolean)) {
    if (out[out.length - 1] !== name) out.push(name);
  }
  return out;
};

/** "Miami, FL, USA" reads as "Miami" on the legs line and in a map tooltip. */
export const stopLabel = (name: string): string => name.split(',')[0].trim() || name;
